/**
 * Test steps in plain words — what the Testing screen shows a person instead
 * of `fill (field=court_forum, value=@not_in_master)`.
 *
 * Mirrors the closed step vocabulary in
 * `backend/app/services/ui_testing/schema.py` (STEPS / EXPECTS / TOKENS). A
 * step name this file does not know falls back to the raw form, so a new step
 * kind still shows — just less politely — until it is added here.
 */

type Step = Record<string, unknown>

/** `LEGAL_CASE_REGISTER` / `court_forum` → `Legal Case Register` / `Court forum`. */
export function nice(name: unknown, titleCase = false): string {
  const s = String(name ?? '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  if (!s) return ''
  const lower = s.toLowerCase()
  if (titleCase) return lower.replace(/\b\w/g, (c) => c.toUpperCase())
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

const field = (v: unknown) => nice(v)
const obj = (v: unknown) => nice(v, true)

const REF = /^@ref:(TC-[A-Z0-9_.-]+)\.([A-Za-z0-9_]+)$/

/** A literal or a run-time token, as a phrase. */
export function valueText(v: unknown): string {
  if (v == null) return 'a value'
  if (typeof v === 'object') return JSON.stringify(v)
  const s = String(v)
  switch (s) {
    case '@synthetic': return 'a valid value'
    case '@lookup': return 'a value from its look-up list'
    case '@not_in_master': return 'a value that is not in its master list'
    case '@today': return 'today’s date'
    case '@now': return 'the current time'
    case '@run_id':
    case '@seq': return 'a unique value'
    case '@blank': return 'nothing'
    case '@filled': return 'the value typed earlier'
  }
  const ref = REF.exec(s)
  if (ref) return `the ${field(ref[2]).toLowerCase()} saved by ${ref[1]}`
  return `“${s}”`
}

const BUILTIN_CLICK: Record<string, string> = {
  add: 'Click Add', edit: 'Click Edit', view: 'Click View', delete: 'Click Delete',
  refresh: 'Click Refresh', save: 'Click Save', save_close: 'Click Save & Close', close: 'Click Close',
  confirm: 'Confirm', cancel: 'Click Cancel', discard: 'Discard the changes', apply: 'Click Apply',
  submit: 'Click Submit', add_line: 'Add a line', delete_line: 'Delete the line',
  approvals: 'Open Approvals', pick: 'Click Pick', ok: 'Click OK', search: 'Click Search',
  export: 'Click Export',
}

const FIELD_STATE: Record<string, string> = {
  protected: 'read-only', editable: 'editable', mandatory: 'required', hidden: 'hidden',
  error: 'marked as an error', visible: 'visible',
}

/** A button as the builder lists it: `save_close` → `Save & Close`, a model
 *  action → its name in words. */
export const actionLabel = (name: string): string =>
  (BUILTIN_CLICK[name] ?? '').replace(/^Click /, '') || nice(name)

/** `mandatory` → `required` — a field state in words. */
export const fieldStateText = (state: string): string => FIELD_STATE[state] ?? nice(state)

const onForm = (s: Step) => (s.form ? ` on the ${nice(s.form).toLowerCase()} tab` : '')

function whereText(where: unknown): string {
  if (!where || typeof where !== 'object') return ''
  const parts = Object.entries(where as Record<string, unknown>)
    .map(([k, v]) => `${field(k).toLowerCase()} ${valueText(v)}`)
  return parts.length ? ` with ${parts.join(' and ')}` : ''
}

function doText(name: string, s: Step): string {
  switch (name) {
    case 'open_screen': return `Open ${obj(s.obj)}${onForm(s)}`
    case 'open_page': return `Open the ${nice(s.page).toLowerCase()} page${s.site ? ` of ${nice(s.site)}` : ''}`
    case 'open_url': return `Go to ${String(s.url ?? '')}`
    case 'click_nav': return `Click ${obj(s.obj)} in the menu`
    case 'click_action': {
      const n = String(s.name ?? '')
      const base = BUILTIN_CLICK[n] ?? `Click the “${nice(n)}” action`
      return `${base}${s.row != null ? ` on row ${String(s.row)}` : ''}${onForm(s)}`
    }
    case 'fill':
      return s.value === '@blank'
        ? `Leave ${field(s.field).toLowerCase()} empty`
        : `Type ${valueText(s.value)} in ${field(s.field).toLowerCase()}`
    case 'select': return `Choose ${valueText(s.value)} in ${field(s.field).toLowerCase()}`
    case 'lookup_pick':
      return `Pick ${s.value != null ? valueText(s.value) : 'a value'} for ${field(s.field).toLowerCase()} from its look-up`
    case 'select_row':
      if (s.where) return `Pick the row${whereText(s.where)}`
      if (s.record) return `Pick the record saved as “${String(s.record)}”`
      return s.index != null ? `Pick row ${String(s.index)} of the list` : 'Pick a row from the list'
    case 'open_tab': return `Open the ${nice(s.form).toLowerCase()} tab`
    case 'set_criteria': return `Set the filter ${field(s.field).toLowerCase()} to ${valueText(s.value)}`
    case 'run_report': return `Run the report${s.format ? ` as ${String(s.format).toUpperCase()}` : ''}`
    case 'submit_form': return `Submit the form${s.values ? ' with the values given' : ''}`
    case 'click_link': return `Click the “${String(s.link ?? '')}” link`
    case 'wait_for':
      return s.ms != null ? `Wait ${Number(s.ms) / 1000} s` : 'Wait for the screen to be ready'
    case 'screenshot': return `Take a screenshot${s.label ? ` (${String(s.label)})` : ''}`
    case 'seed_record': return `Create a ${obj(s.obj)} record to work with`
  }
  return ''
}

function expectText(name: string, s: Step): string {
  switch (name) {
    case 'message': {
      const kind = String(s.kind ?? 'error')
      const a = kind === 'error' || kind === 'info' ? 'An' : 'A'
      if (s.absent) return `No ${kind} message appears`
      return `${a} ${kind} message appears${s.contains ? ` saying “${String(s.contains)}”` : ''}`
    }
    case 'field_value': {
      const f = field(s.field)
      if (s.equals != null) return `${f} shows ${valueText(s.equals)}`
      if (s.contains != null) return `${f} contains “${String(s.contains)}”`
      return `${f} has a value`
    }
    case 'field_state':
      return `${field(s.field)} is ${FIELD_STATE[String(s.state)] ?? String(s.state)}`
    case 'record':
      return s.record
        ? `The record “${String(s.record)}” is in ${obj(s.obj)}`
        : `The ${obj(s.obj)} record is saved${whereText(s.where)}`
    case 'no_record':
      return s.record
        ? `The record “${String(s.record)}” is no longer in ${obj(s.obj)}`
        : `No ${obj(s.obj)} record is saved${whereText(s.where)}`
    case 'row_count':
      if (s.equals != null) return `The list shows ${String(s.equals)} rows`
      if (s.min != null && s.max != null) return `The list shows ${String(s.min)}–${String(s.max)} rows`
      if (s.min != null) return `The list shows at least ${String(s.min)} rows`
      if (s.max != null) return `The list shows at most ${String(s.max)} rows`
      return 'The list shows rows'
    case 'visual_has_data':
      return s.allow_empty ? 'The visual opens without an error' : 'The visual shows data'
    case 'text_visible': return `“${String(s.text ?? '')}” is shown on the page`
    case 'url': return `The address contains “${String(s.contains ?? '')}”`
    case 'no_console_errors': return 'The browser console shows no errors'
    case 'no_failed_requests': return 'No call to the server fails'
    case 'download': return `A ${s.kind ? `${String(s.kind).toUpperCase()} ` : ''}file is downloaded`
    case 'nav_matches_grants': return 'The menu shows only what this role may open'
    case 'links_ok': return 'Every link on the page works'
    case 'dialog':
      return s.open === false
        ? `The “${nice(s.name)}” dialog is closed`
        : `The “${nice(s.name)}” dialog is open`
  }
  return ''
}

/** The raw form — the fallback for a step kind this file does not know. */
export function rawStep(step: Step): string {
  const name = String(step.do ?? step.expect ?? '?')
  const args = Object.entries(step)
    .filter(([k]) => !['do', 'expect', 'note'].includes(k))
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(', ')
  return `${'expect' in step ? 'expect ' : ''}${name}${args ? ` (${args})` : ''}`
}

/** True for a check (`expect`) rather than an action (`do`). */
export const isCheck = (step: unknown) =>
  !!step && typeof step === 'object' && 'expect' in (step as Step)

/** One step as a sentence. */
export function stepText(step: unknown): string {
  if (!step || typeof step !== 'object') return String(step ?? '')
  const s = step as Step
  try {
    const text = 'expect' in s ? expectText(String(s.expect), s) : doText(String(s.do), s)
    return text || rawStep(s)
  } catch {
    return rawStep(s)
  }
}

/** What a failed step should have done, as one sentence. */
export function shouldHappen(step: unknown): string {
  if (!step || typeof step !== 'object') return ''
  return isCheck(step) ? `${stepText(step)}.` : `“${stepText(step)}” works.`
}

const CASE_TYPE: Record<string, string> = {
  smoke: 'Opens without errors', positive: 'Should work', negative: 'Should be refused',
  role: 'Role check', journey: 'End-to-end journey', boundary: 'Edge values',
}
export const caseTypeText = (t?: string) => CASE_TYPE[t || 'positive'] ?? nice(t)

export const personaText = (p?: string) => (!p || p === 'owner' ? 'As owner' : `As ${nice(p)}`)

const ORIGIN: Record<string, string> = { seed: 'Generated', agent: 'Written by AI', user: 'Edited by a person' }
export const originText = (o?: string) => ORIGIN[o || 'agent'] ?? nice(o)
